/* @ngInject */
export default function CameraService($sails, $q, $log, EquipmentInfoService) {
  const service = this;
  service.model = { panels: {} };

  const qs = (params = {}) => Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');

  const api = (verb, url, info) => $sails[verb](url, info).catch(logError);
  const action = (name, id, body) => api('post', `/equipment/${id}/actions/${name}`, _.merge({ id }, body));
  const status = (name, id, query) => api('get', `/equipment/${id}/statuses/${name}?${qs(query)}`);

  service.setPanelGetEquipment = (...args) => $q.when(setPanelGetEquipment(...args));
  service.getCamerasForPanel = panelId => _.get(service, ['model', 'panels', panelId], null);
  service.getCamera = (panelId, cameraId) => _.get(service, ['model', 'panels', panelId, cameraId], null);
  service.deletePanel = panelId => _.unset(service, ['model', 'panels', panelId]);

  service.retrieveStreamViews = (...args) => $q.when(retrieveStreamViews(...args));
  service.getZoom = (id, camera) => status('getCameraZoom', id, { camera });
  service.getZoomFocus = status.bind(null, 'getCameraZoomAndFocus');
  service.getZones = status.bind(null, 'getAllMotionZoneInfo');
  service.getRotation = status.bind(null, 'getCameraRotation');
  service.getResolution = status.bind(null, 'getFieldResolution');
  service.getCameraResolutions = status.bind(null, 'getCameraResolutions');
  service.getLiveSettings = status.bind(null, 'getFieldSettings'); // RCU expects getFieldSettings
  service.getEventSettings = status.bind(null, 'getMotionSettings'); // RCU expects getMotionSettings
  service.getRecordingSettings = status.bind(null, 'getRecordingSettings');
  service.getAudioSettings = status.bind(null, 'getAudioSettings');
  service.getAudioAlertsSettings = status.bind(null, 'getAudioDetectionInfo');
  service.getAudioLevel = status.bind(null, 'getAudioLevel');
  service.getIRCutFilter = status.bind(null, 'getIRCutFilter');
  service.getListOfVideos = getListOfVideos;
  service.getAllVmd4Info = status.bind(null, 'getAllVmd4InfoRotated');
  service.getMotionDetectionConfig = status.bind(null, 'getMotionDetectionConfig');
  service.getDefaultAspectRatio = status.bind(null, 'getDefaultAspectRatio');

  service.resetCamera = id => api('post', `/equipment/${id}/actions/resetCamera?timeout=120000`, { id });
  service.autoFocus = action.bind(null, 'autofocusCamera');
  service.getSnapshot = (id, camera) => action('takeSnapshot', id, { camera });
  service.restartCamera = action.bind(null, 'restartCamera');
  service.setZoom = (id, zoom, camera) => action('setCameraZoom', id, { zoom, camera });
  service.setFocus = (id, focus) => action('setCameraFocus', id, { focus });
  service.saveZone = action.bind(null, 'setMotionZone');
  service.setMotionDetectionConfig = action.bind(null, 'setMotionDetectionConfig');
  service.saveRotation = (id, rotation) => action('setCameraRotation', id, { rotation });
  service.saveLiveSettings = (id, settings) => action('setFieldSettings', id, { settings });
  service.saveEventSettings = (id, settings) => action('setMotionSettings', id, { settings });
  service.saveRecordingSettings = (id, settings) => action('setRecordingSettings', id, { settings });
  service.setAudioSettings = action.bind(null, 'setAudioSettings');
  service.setAudioDetection = action.bind(null, 'setAudioDetection');
  service.setIRCutFilter = (id, state) => action('setIRCutFilter', id, { state });
  service.resetIRCutFilter = (id, state) => action('resetIRCutFilter', id, { state });
  service.setAutoFocus = (id, autofocus) => action('setAutofocus', id, { autofocus });
  service.syncVmd4Conf = action.bind(null, 'syncVmd4ConfRotated');
  service.addVmd4Profile = action.bind(null, 'addVmd4Profile');
  service.testAlarm = (id, profile) => action('sendAlarmEvent', id, { profile });
  service.hasMultiViews = hasMultiViews;


  async function setPanelGetEquipment(panelId, equipment) {
    const cameras = _.keyBy(await EquipmentInfoService.getFullEquipment(equipment), 'id');
    _.set(service, ['model', 'panels', panelId], cameras);
    return cameras;
  }

  async function retrieveStreamViews(eq) {
    let camsAndViews;
    try {
      ({ data: camsAndViews } = await status('getViews', eq.id));
    } catch {
      camsAndViews = {
        cameras: ['1'],
        views: ['1']
      };
    }
    camsAndViews.views = _.reduce(camsAndViews.views, (acc, viewId) =>
      _.set(acc, viewId, _.get(eq, ['settings', 'views', viewId], { name: `Stream ${viewId}` }))
    , {});
    camsAndViews.cameras = _.reduce(camsAndViews.cameras, (acc, cameraId) =>
      _.set(acc, cameraId, _.get(eq, ['settings', 'cameras', cameraId], { name: `Camera ${cameraId}` }))
    , {});
    eq.camsAndViews = camsAndViews;
    return eq.camsAndViews;
  }

  function getListOfVideos(id, startDate, endDate, camera) {
    let url = `/equipment/${id}/statuses/getContinuousVideos`;
    url += '?timeout=60000';
    url += startDate ? `&start=${startDate.toISOString()}` : '';
    url += endDate ? `&end=${endDate.toISOString()}` : '';
    url += camera ? `&cameraNum=${camera}` : '';
    return api('get', url);
  }

  function logError(error) {
    $log.error('Error', error);
    return $q.reject(error.data);
  }

  function hasMultiViews(panelId, cameraId) {
    const camera = service.getCamera(panelId, cameraId);
    return _.size(_.get(camera, ['camsAndViews', 'views'])) > 1;
  }
}
